import React, { FC, useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { sessionStore } from 'src/store/sessionStore';
import { Modal, Spin } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { toast } from 'src/utils/appAlert';
import { auth } from 'src/config/firebase';
import { signOut } from 'firebase/auth';
import { useQueryClient } from '@tanstack/react-query';
import './Sidebar.scss';
import { ReactComponent as SidebarNavIcon } from '../../../../assets/images/onboarding-trainer/components/sidebar/sidebar-nav.svg';
import { ReactComponent as NewProgramIcon } from '../../../../assets/images/onboarding-trainer/components/sidebar/new-program.svg';
import { ReactComponent as ProgramsIcon } from '../../../../assets/images/onboarding-trainer/components/sidebar/programs.svg';
import { ReactComponent as MessagesIcon } from '../../../../assets/images/onboarding-trainer/components/sidebar/messages.svg';
import { ReactComponent as CloseIcon } from '../../../../assets/images/onboarding-trainer/components/sidebar/close.svg';
import { ReactComponent as ClientsIcon } from '../../../../assets/images/onboarding-trainer/components/sidebar/clients.svg';
import { ReactComponent as LargeIcon } from '../../../../assets/images/onboarding-trainer/components/sidebar/large.svg';
import { ReactComponent as SmallIcon } from '../../../../assets/images/onboarding-trainer/components/sidebar/small.svg';
import { ReactComponent as OnlyLogoIcon } from '../../../../assets/images/onboarding-trainer/components/sidebar/only-logo.svg';
import { ReactComponent as LogoIcon } from '../../../../assets/images/onboarding-trainer/logo.svg';
import { ReactComponent as SubscriptionPlanMenuSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/profile-menu/subscription-plan-menu.svg';
import { ReactComponent as ProfileMenuSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/profile-menu/profile-menu.svg';
import { ReactComponent as LogoutMenuSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/profile-menu/logout-menu.svg';
import { ReactComponent as UserChevronLeftSVG } from 'src/assets/images/onboarding-trainer/components/sidebar/user-chevron-left.svg';
import { ReactComponent as UserChevronRightSVG } from 'src/assets/images/onboarding-trainer/subscription-icons/billing-history/manage-payment-method-next.svg';
import { useGetTrainerUnreadMessages } from 'src/api/mutations/useGetTrainerUnreadMessages';
import { useChatRealtime } from 'src/hooks/useChatRealtime';
import { useInquiriesRealtime } from 'src/hooks/useInquiriesRealtime';
import { useCheckinsRealtime } from 'src/hooks/useCheckinsRealtime';
import { setTrainerOnlineStatus } from 'src/services/presence/trainerPresence';
import { unsubscribeBrowserPush } from 'src/services/push/registerPush';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
}

export const Sidebar: FC<SidebarProps> = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const { currentUser, logout } = sessionStore();
  const [isLogoutModalVisible, setIsLogoutModalVisible] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (profileMenuRef.current && !profileMenuRef.current.contains(e.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };
    if (isProfileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileMenuOpen]);

  useEffect(() => {
    setIsProfileMenuOpen(false);
  }, [location.pathname]);

  const isDashboardScreens =
    location.pathname.includes('/dashboard/clients') ||
    location.pathname.includes('/dashboard/programs') ||
    location.pathname.includes('/dashboard/messages');

  const trainerUid = currentUser?.uid || '';
  const { data: unreadData } = useGetTrainerUnreadMessages();
  const { totalUnread: chatUnread } = useChatRealtime();
  const { inquiries } = useInquiriesRealtime(trainerUid);
  const { checkins } = useCheckinsRealtime(trainerUid);

  const unreadInquiries = inquiries.filter((inq) => {
    const isSentByTrainer =
      inq.message?.sender_role === 'trainer' ||
      inq.message?.sender_uid === trainerUid ||
      inq.message?.send_uid === trainerUid;
    return !isSentByTrainer && !inq.message?.is_read;
  }).length;
  const unreadCheckins = checkins.filter(
    (c) => !c.is_read && (c.isAppoint || Boolean(c.appointUId)),
  ).length;

  // Realtime total unread overrides the initial fetch if available.
  const totalRealtimeUnread = (chatUnread || 0) + unreadInquiries + unreadCheckins;
  const displayUnread =
    totalRealtimeUnread > 0 ? totalRealtimeUnread : unreadData?.totalUnread || 0;

  const closeSidebarOnMobile = () => {
    if (window.innerWidth < 992) {
      onClose();
    }
  };

  const handleNewProgram = () => {
    closeSidebarOnMobile();
    if (isDashboardScreens) {
      navigate('/onboarding-trainer/dashboard');
    } else {
      navigate('/onboarding-trainer/dashboard/program-generator/ai?action=new_program');
    }
  };

  const calculateProgress = () => {
    if (!currentUser) return 0;

    let score = 0;
    const totalFields = 11;

    if (currentUser.name && currentUser.surname) score += 1;
    if (currentUser.bio) score += 1;
    if (currentUser.experience) score += 1;
    if (currentUser.onboarding?.step3_offers && currentUser.onboarding.step3_offers.length > 0)
      score += 1;
    if (currentUser.certificates && currentUser.certificates.length > 0) score += 1;
    if (currentUser.expertise) score += 1;
    if (currentUser.trainingStyle) score += 1;
    if (currentUser.pricing && currentUser.pricing.length > 0) score += 1;
    if (currentUser.location) score += 1;
    if (currentUser.facility) score += 1;
    if (currentUser.introductionVideoUrl) score += 1;

    const rawPercentage = (score / totalFields) * 100;
    return Math.round(rawPercentage / 5) * 5;
  };

  const progress = calculateProgress();
  const getInitials = () => {
    if (!currentUser) return 'U';
    const first = currentUser.name?.[0] || '';
    const last =
      currentUser.surname?.[0] ||
      (currentUser.name && currentUser.name.includes(' ')
        ? currentUser.name.trim().split(/\s+/)[1]?.[0] || ''
        : '');
    return `${first}${last}`.toUpperCase() || 'U';
  };

  const handleLogout = async () => {
    if (isLoggingOut) return;
    setIsLoggingOut(true);
    try {
      if (currentUser?.uid) {
        await Promise.allSettled([
          setTrainerOnlineStatus(currentUser.uid, false),
          unsubscribeBrowserPush(currentUser.uid, 'trainer'),
        ]);
      }
      await signOut(auth);
      queryClient.clear();
      logout();
      setIsLogoutModalVisible(false);
      toast.success('Logged out successfully!', {
        position: 'top-right',
        style: {
          borderRadius: '8px',
          background: '#0a0b24',
          color: '#fff',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          minWidth: 'max-content',
        },
      });
      navigate('/onboarding-trainer/login');
    } catch (error) {
      console.error('Logout Error:', error);
      toast.error('Failed to log out. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && <div className="sidebar-overlay" onClick={onClose}></div>}

      {/* Sidebar Container */}
      <aside
        className={`trainer-sidebar ${isOpen ? 'open' : ''} ${isCollapsed ? 'collapsed' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Top Section */}
        <div className="sidebar-top">
          {/* Logo and Mobile Close */}
          <div className="sidebar-header">
            <div
              className="logo-container"
              onClick={() => {
                if (onToggleCollapse) onToggleCollapse();
                closeSidebarOnMobile();
              }}
              style={{ cursor: 'pointer' }}
            >
              {isCollapsed ? <OnlyLogoIcon /> : <LogoIcon />}
            </div>

            {/* Desktop toggle button */}
            {!isCollapsed && (
              <button className="desktop-toggle-btn" onClick={onToggleCollapse}>
                {<SidebarNavIcon />}
              </button>
            )}

            {/* Mobile close button */}
            <button className="mobile-close-btn" onClick={onClose}>
              {<CloseIcon />}
            </button>
          </div>

          {/* New Program Button */}
          <div className="sidebar-action">
            <button className="new-program-btn" onClick={handleNewProgram}>
              {<NewProgramIcon />}
              {!isCollapsed && <span>{'Create'}</span>}
            </button>
          </div>

          {/* Navigation Links */}
          <nav className="sidebar-nav">
            <NavLink
              to="/onboarding-trainer/dashboard/clients"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={closeSidebarOnMobile}
            >
              {<ClientsIcon />}
              {!isCollapsed && <span>Clients</span>}
            </NavLink>

            <NavLink
              to="/onboarding-trainer/dashboard/programs"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={closeSidebarOnMobile}
            >
              {<ProgramsIcon />}
              {!isCollapsed && <span>Programs</span>}
            </NavLink>

            <NavLink
              to="/onboarding-trainer/dashboard/messages"
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              onClick={closeSidebarOnMobile}
            >
              <div className="nav-icon-wrapper">
                <MessagesIcon />
                {isCollapsed && displayUnread > 0 && <span className="unread-dot-badge" />}
              </div>
              {!isCollapsed && <span>Messages</span>}
              {!isCollapsed && displayUnread > 0 && (
                <div className="unread-badge">{displayUnread > 99 ? '99+' : displayUnread}</div>
              )}
            </NavLink>
          </nav>
        </div>

        {/* Bottom Section - User Profile */}
        <div className="sidebar-bottom" ref={profileMenuRef}>
          {/* Floating Profile Popup Menu */}
          {isProfileMenuOpen && (
            <div className="profile-popup-menu">
              {/*<button*/}
              {/*  type="button"*/}
              {/*  className="profile-menu-item"*/}
              {/*  onClick={() => {*/}
              {/*    setIsProfileMenuOpen(false);*/}
              {/*    closeSidebarOnMobile();*/}
              {/*    navigate('/onboarding-trainer/dashboard/subscription');*/}
              {/*  }}*/}
              {/*>*/}
              {/*  <div className="menu-icon-wrap">*/}
              {/*    <SubscriptionPlanMenuSVG />*/}
              {/*  </div>*/}
              {/*  <span>{'Subscription Plan'}</span>*/}
              {/*</button>*/}

              <button
                type="button"
                className="profile-menu-item"
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  closeSidebarOnMobile();
                  navigate('/onboarding-trainer/dashboard/profile');
                }}
              >
                <div className="menu-icon-wrap">
                  <ProfileMenuSVG />
                </div>
                <span>{'Profile'}</span>
              </button>

              <button
                type="button"
                className="profile-menu-item logout-item"
                onClick={() => {
                  setIsProfileMenuOpen(false);
                  setIsLogoutModalVisible(true);
                }}
              >
                <div className="menu-icon-wrap">
                  <LogoutMenuSVG />
                </div>
                <span>{'Logout'}</span>
              </button>
            </div>
          )}

          <div
            className={`user-profile ${isProfileMenuOpen ? 'menu-active' : ''}`}
            onClick={() => setIsProfileMenuOpen((prev) => !prev)}
            role="button"
            tabIndex={0}
          >
            <div
              className="avatar"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#fff',
                fontWeight: 'bold',
                backgroundImage: currentUser?.avatarUrl ? `url(${currentUser.avatarUrl})` : 'none',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                color: currentUser?.avatarUrl ? 'transparent' : '#060723',
              }}
            >
              {/* Fallback avatar to initials */}
              {!currentUser?.avatarUrl && getInitials()}
            </div>
            {!isCollapsed && (
              <>
                <div className="user-info">
                  <div className="user-name">
                    {currentUser
                      ? `${currentUser.name || ''} ${currentUser.surname || ''}`.trim() ||
                        'Guest User'
                      : 'Guest User'}
                  </div>
                  <div className="user-progress">
                    <span>{progress}%</span>
                    <div className="progress-bar-container">
                      <div className="progress-bar" style={{ width: `${progress}%` }}></div>
                    </div>
                  </div>
                </div>
                <div className={`user-arrow ${isProfileMenuOpen ? 'menu-open' : 'menu-closed'}`}>
                  {isProfileMenuOpen ? <UserChevronLeftSVG /> : <UserChevronRightSVG />}
                </div>
              </>
            )}
          </div>
        </div>
      </aside>

      <Modal
        open={isLogoutModalVisible}
        footer={null}
        onCancel={() => {
          if (!isLoggingOut) setIsLogoutModalVisible(false);
        }}
        centered
        width={420}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">{<LargeIcon />}</div>
          <h2>Log out?</h2>
          <p>Are you sure you want to log out of your account?</p>
          <div className="logout-actions">
            <button
              className="btn-logout"
              onClick={handleLogout}
              disabled={isLoggingOut}
              style={{
                opacity: isLoggingOut ? 0.8 : 1,
                cursor: isLoggingOut ? 'not-allowed' : 'pointer',
              }}
            >
              {isLoggingOut ? (
                <>
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 16, color: '#060723' }} spin />}
                  />
                  <span>Logging out...</span>
                </>
              ) : (
                <>
                  <SmallIcon /> Log out
                </>
              )}
            </button>
            <button
              className="btn-cancel"
              onClick={() => setIsLogoutModalVisible(false)}
              disabled={isLoggingOut}
              style={{
                opacity: isLoggingOut ? 0.5 : 1,
                cursor: isLoggingOut ? 'not-allowed' : 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};
